import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Monitor, Smartphone, Loader2, AlertCircle, MessageSquarePlus,
  MessageSquare, X, Check, Trash2, CornerDownLeft, CheckCircle2, Reply as ReplyIcon,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandedLoader } from "@/components/BrandedLoader";
import { DeviceMock } from "@/components/DeviceMock";
import { Tutorial } from "@/components/Tutorial";
import { BRAND } from "@/lib/branding";
import { getStrings } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const L = getStrings();

const DEVICES = {
  desktop: { w: "100%", maxW: "1280px", radius: 12, border: 1 },
  mobile: { w: "390px", maxW: "390px", radius: 40, border: 10 },
};

export default function SharedPreview() {
  const { shareId } = useParams();
  const [preview, setPreview] = useState(null);
  const [device, setDevice] = useState("mobile");
  const [status, setStatus] = useState("loading");

  const [comments, setComments] = useState([]);
  const [commentMode, setCommentMode] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState(null); // {x,y}
  const [draftText, setDraftText] = useState("");
  const [activePin, setActivePin] = useState(null); // comment id
  const [author, setAuthor] = useState(() => localStorage.getItem("preview_author") || "");
  const [email, setEmail] = useState(() => localStorage.getItem("preview_email") || "");
  const [booting, setBooting] = useState(true);
  const [showTour, setShowTour] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const fallback = setTimeout(() => setBooting(false), 20000);
    return () => clearTimeout(fallback);
  }, []);

  const handleFrameLoad = () => setTimeout(() => setBooting(false), 1400);

  const loadComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shared/${shareId}/comments`);
      setComments(res.data);
    } catch (e) { /* silent */ }
  }, [shareId]);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/shared/${shareId}`)
      .then((res) => {
        if (!mounted) return;
        setPreview(res.data);
        setStatus("ok");
        if (!localStorage.getItem("cortex_client_tour_done")) {
          setTimeout(() => setShowTour(true), 900);
        }
      })
      .catch(() => mounted && setStatus("error"));
    loadComments();
    return () => { mounted = false; };
  }, [shareId, loadComments]);

  const closeTour = () => {
    localStorage.setItem("cortex_client_tour_done", "1");
    setShowTour(false);
  };

  const d = DEVICES[device] || DEVICES.desktop;
  const proxied = `${BACKEND_URL}/api/proxy/id/${shareId}`;

  const handleOverlayClick = (e) => {
    if (!commentMode) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setActivePin(null);
    setDraft({ x, y });
    setDraftText("");
  };

  const submitDraft = async () => {
    const text = draftText.trim();
    if (!text) { toast.error(L.writeNoteFirst); return; }
    try {
      const name = author.trim() || "Guest";
      const mail = email.trim();
      localStorage.setItem("preview_author", name);
      if (mail) localStorage.setItem("preview_email", mail);
      await axios.post(`${API}/shared/${shareId}/comments`, {
        x: draft.x, y: draft.y, device, text, author: name, author_email: mail || undefined, source_url: window.location.href,
      });
      setDraft(null);
      setDraftText("");
      await loadComments();
      toast.success(L.commentAdded);
    } catch (e) {
      toast.error(L.couldNotAdd);
    }
  };

  const toggleResolve = async (id) => {
    try {
      await axios.patch(`${API}/comments/${id}/resolve`);
      await loadComments();
    } catch (e) { toast.error(L.couldNotUpdate); }
  };

  const removeComment = async (id) => {
    try {
      await axios.delete(`${API}/comments/${id}`);
      setActivePin(null);
      await loadComments();
      toast.success(L.commentRemoved);
    } catch (e) { toast.error(L.couldNotDelete); }
  };

  const approve = async () => {
    if (preview?.approved) return;
    try {
      const name = (author.trim() || localStorage.getItem("preview_author") || "").trim();
      const res = await axios.post(`${API}/shared/${shareId}/approve`, {
        name: name || "A client", source_url: window.location.href,
      });
      setPreview((p) => ({ ...p, ...res.data }));
      toast.success(L.approvedToast);
    } catch (e) { toast.error(L.couldNotApprove); }
  };

  const submitReply = async (commentId, text, done) => {
    const t = text.trim();
    if (!t) return;
    try {
      const name = author.trim() || "Guest";
      localStorage.setItem("preview_author", name);
      await axios.post(`${API}/comments/${commentId}/replies`, { author: name, text: t, source_url: window.location.href });
      await loadComments();
      done?.();
    } catch (e) { toast.error(L.couldNotReply); }
  };

  return (
    <div className="flex flex-col h-screen relative">
      <div className="grain-overlay" />

      <header className="sticky top-0 z-50 flex flex-col gap-3 sm:flex-row sm:items-center px-4 md:px-8 py-3 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="flex items-center gap-3 shrink-0">
          <Link to="/" className="shrink-0"><BrandLogo /></Link>
          {preview?.approved ? (
            <div className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-semibold bg-emerald-500/15 border border-emerald-500/40 text-emerald-400" data-testid="approved-badge">
              <CheckCircle2 className="h-4 w-4" />
              {L.approved}
            </div>
          ) : (
            <button
              onClick={approve}
              data-testid="approve-button"
              className="inline-flex items-center h-9 px-4 rounded-lg text-sm font-semibold bg-emerald-500 text-[#04120a] hover:bg-emerald-400 transition-colors"
            >
              {L.approveDesign}
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <button
            onClick={() => { setCommentMode((v) => !v); setDraft(null); }}
            data-testid="comment-mode-toggle"
            title={L.commentModeHint}
            className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-semibold border transition-colors ${
              commentMode ? "bg-emerald-500 text-[#04120a] border-emerald-500" : "border-white/15 bg-white/5 text-white hover:bg-white/10"
            }`}
          >
            <MessageSquarePlus className="h-4 w-4" />
            <span className="inline">{commentMode ? L.tapToAdd : L.leaveFeedback}</span>
          </button>
          <button
            onClick={() => setPanelOpen((v) => !v)}
            data-testid="comments-panel-toggle"
            title={L.viewAllFeedback}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium border border-white/10 bg-black/50 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">{L.notes}</span>
            <span className="text-white/50">{comments.length}</span>
          </button>
          <div className="flex items-center rounded-lg border border-white/10 bg-black/50 p-1" data-testid="shared-device-toggle">
            <button onClick={() => setDevice("desktop")} data-testid="shared-desktop-toggle"
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-sm font-medium transition-colors ${device === "desktop" ? "bg-white text-black" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <Monitor className="h-4 w-4" /><span className="inline">{L.desktop}</span>
            </button>
            <button onClick={() => setDevice("mobile")} data-testid="shared-mobile-toggle"
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-sm font-medium transition-colors ${device === "mobile" ? "bg-white text-black" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <Smartphone className="h-4 w-4" /><span className="inline">{L.mobile}</span>
            </button>
          </div>
        </div>
      </header>

      {commentMode && status === "ok" && (
        <div className="relative z-40 flex justify-center pointer-events-none">
          <div className="absolute top-2 inline-flex items-center gap-2 rounded-full bg-emerald-500 text-[#04120a] text-xs font-semibold px-3.5 py-1.5 shadow-lg" data-testid="comment-hint">
            <MessageSquarePlus className="h-3.5 w-3.5" />
            {L.pinHint}
          </div>
        </div>
      )}

      <div className="flex-1 relative z-10 flex overflow-hidden">
        <main className="flex-1 flex items-center justify-center overflow-auto p-6 md:p-10 dotgrid">
          {status === "loading" && <Loader2 className="h-6 w-6 animate-spin text-white/60" data-testid="shared-loading" />}
          {status === "error" && (
            <div className="flex flex-col items-center text-center max-w-sm" data-testid="shared-error">
              <div className="h-14 w-14 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center mb-5">
                <AlertCircle className="h-6 w-6 text-white/60" />
              </div>
              <h2 className="font-display text-xl font-semibold text-white">{L.previewNotFound}</h2>
              <p className="mt-2 text-sm text-white/50">{L.previewInvalid}</p>
              <Link to="/" className="mt-5 text-sm text-white underline underline-offset-4 hover:text-white/80 transition-colors">
                {L.goTo(BRAND.name)}
              </Link>
            </div>
          )}
          {status === "ok" && (
            <DeviceMock device={device} testid="shared-frame-container">
              <AnimatePresence>
                {booting && <BrandedLoader key="shared-loader" />}
              </AnimatePresence>
              <iframe
                src={proxied}
                title="shared-preview"
                onLoad={handleFrameLoad}
                className="w-full h-full bg-white"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-presentation"
                referrerPolicy="no-referrer"
                data-testid="shared-iframe"
              />

              {/* Comment overlay */}
              <div
                ref={overlayRef}
                onClick={handleOverlayClick}
                data-testid="comment-overlay"
                className={`absolute inset-0 z-10 ${commentMode ? "cursor-crosshair" : "pointer-events-none"}`}
              >
                {comments.map((c, i) => (
                  <Pin
                    key={c.id}
                    index={i + 1}
                    comment={c}
                    active={activePin === c.id}
                    onOpen={() => { setActivePin(activePin === c.id ? null : c.id); setDraft(null); }}
                    onResolve={() => toggleResolve(c.id)}
                    onDelete={() => removeComment(c.id)}
                  />
                ))}

                {draft && (
                  <div
                    className="absolute z-40 pointer-events-none"
                    style={{ left: `${draft.x}%`, top: `${draft.y}%` }}
                    data-testid="comment-draft-marker"
                  >
                    <span className="-translate-x-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-black text-sm font-semibold shadow-lg ring-2 ring-white/40 animate-pulse">+</span>
                  </div>
                )}
              </div>
            </DeviceMock>
          )}
        </main>

        {/* Comments side panel */}
        <AnimatePresence>
          {panelOpen && (
            <motion.aside
              initial={{ x: 340, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 340, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 34 }}
              className="w-[340px] shrink-0 border-l border-white/10 bg-[#080809] flex flex-col"
              data-testid="comments-panel"
            >
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10">
                <p className="font-display text-sm font-semibold">{L.feedback} ({comments.length})</p>
                <button onClick={() => setPanelOpen(false)} className="text-white/40 hover:text-white transition-colors"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex-1 overflow-auto p-3 space-y-2">
                {comments.length === 0 ? (
                  <p className="text-sm text-white/40 text-center py-8 px-4">
                    {L.noFeedbackA} <span className="text-white/70">{L.leaveFeedback}</span> {L.noFeedbackB}
                  </p>
                ) : comments.map((c, i) => (
                  <CommentCard
                    key={c.id}
                    index={i + 1}
                    comment={c}
                    onSelect={() => { setActivePin(c.id); }}
                    onResolve={() => toggleResolve(c.id)}
                    onDelete={() => removeComment(c.id)}
                    onReply={(text, done) => submitReply(c.id, text, done)}
                  />
                ))}
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* Always-visible feedback composer (rendered outside the clipped device shell) */}
      <AnimatePresence>
        {draft && (
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-3 pb-4 sm:pb-6 pointer-events-none"
            data-testid="comment-draft"
          >
            <div className="pointer-events-auto w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0b]/95 backdrop-blur-xl p-4 shadow-[0_-10px_40px_-10px_rgba(0,0,0,0.9)]">
              <div className="flex items-center gap-2 mb-3">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-black text-xs font-semibold">+</span>
                <p className="text-sm font-semibold text-white">{L.leaveFeedbackHere}</p>
                <button onClick={() => setDraft(null)} className="ml-auto text-white/40 hover:text-white transition-colors" data-testid="comment-cancel-x"><X className="h-4 w-4" /></button>
              </div>
              <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder={L.yourName}
                className="w-full mb-2 h-9 rounded-lg bg-black/50 border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/30"
                data-testid="comment-author-input"
              />
              <textarea
                autoFocus
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitDraft(); }}
                placeholder={L.describeChange}
                rows={3}
                className="w-full resize-none rounded-lg bg-black/50 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/30"
                data-testid="comment-text-input"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[11px] text-white/30 flex items-center gap-1"><CornerDownLeft className="h-3 w-3" />{L.enterToPost}</span>
                <div className="flex gap-2">
                  <button onClick={() => setDraft(null)} className="h-9 px-3.5 rounded-lg text-sm text-white/60 hover:bg-white/10 transition-colors" data-testid="comment-cancel">{L.cancel}</button>
                  <button onClick={submitDraft} className="h-9 px-4 rounded-lg text-sm font-semibold bg-emerald-500 text-[#04120a] hover:bg-emerald-400 transition-colors" data-testid="comment-submit">{L.postFeedback}</button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTour && <Tutorial onClose={closeTour} />}
      </AnimatePresence>
    </div>
  );
}

const Pin = ({ index, comment, active, onOpen, onResolve, onDelete }) => (
  <div className="absolute pointer-events-auto" style={{ left: `${comment.x}%`, top: `${comment.y}%` }}>
    <button
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      data-testid={`pin-${comment.id}`}
      className={`-translate-x-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-xs font-semibold flex items-center justify-center shadow-lg ring-2 transition-transform hover:scale-110 ${
        comment.resolved ? "bg-emerald-500 text-white ring-emerald-300/40" : "bg-white text-black ring-white/40"
      }`}
    >
      {comment.resolved ? <Check className="h-3.5 w-3.5" /> : index}
    </button>
    {active && (
      <div
        className="absolute left-3 top-3 z-40 w-60 rounded-xl border border-white/10 bg-[#0a0a0b] p-3 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid={`pin-popover-${comment.id}`}
      >
        <p className="text-xs font-medium text-white mb-1">{comment.author}</p>
        <p className="text-sm text-white/80">{comment.text}</p>
        {comment.replies?.length > 0 && (
          <div className="mt-2 space-y-1.5 border-l-2 border-white/10 pl-2">
            {comment.replies.map((r, idx) => (
              <div key={idx}>
                <p className="text-[10px] text-white/40">{r.author}</p>
                <p className="text-xs text-white/70">{r.text}</p>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2.5 flex gap-3 border-t border-white/10 pt-2">
          <button onClick={onResolve} className="text-[11px] text-white/50 hover:text-emerald-400 inline-flex items-center gap-1 transition-colors">
            <Check className="h-3 w-3" /> {comment.resolved ? L.reopen : L.resolve}
          </button>
          <button onClick={onDelete} className="text-[11px] text-white/50 hover:text-red-400 inline-flex items-center gap-1 transition-colors">
            <Trash2 className="h-3 w-3" /> {L.del}
          </button>
        </div>
      </div>
    )}
  </div>
);

const CommentCard = ({ index, comment, onSelect, onResolve, onDelete, onReply }) => {
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  return (
    <div
      onClick={onSelect}
      className={`rounded-lg border p-3 cursor-pointer transition-colors ${comment.resolved ? "border-emerald-500/20 bg-emerald-500/5" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
      data-testid={`comment-card-${comment.id}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="h-5 w-5 rounded-full bg-white text-black text-[10px] font-semibold flex items-center justify-center">{index}</span>
        <span className="text-xs font-medium text-white truncate">{comment.author}</span>
        {comment.resolved && <span className="ml-auto text-[10px] text-emerald-400">{L.resolved}</span>}
      </div>
      <p className={`text-sm ${comment.resolved ? "text-white/40 line-through" : "text-white/80"}`}>{comment.text}</p>

      {comment.replies?.length > 0 && (
        <div className="mt-2.5 space-y-2 border-l-2 border-white/10 pl-2.5" data-testid={`replies-${comment.id}`}>
          {comment.replies.map((r, idx) => (
            <div key={idx}>
              <p className="text-[10px] text-white/40">{r.author}</p>
              <p className="text-xs text-white/75">{r.text}</p>
            </div>
          ))}
        </div>
      )}

      {replying ? (
        <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
          <textarea
            autoFocus
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { onReply(replyText, () => { setReplyText(""); setReplying(false); }); } }}
            placeholder={L.writeReply}
            rows={2}
            className="w-full resize-none rounded-md bg-black/50 border border-white/10 px-2.5 py-2 text-xs text-white placeholder:text-white/30 outline-none focus:border-white/30"
            data-testid={`reply-input-${comment.id}`}
          />
          <div className="mt-1.5 flex justify-end gap-1.5">
            <button onClick={() => { setReplying(false); setReplyText(""); }} className="h-7 px-2.5 rounded-md text-xs text-white/60 hover:bg-white/10 transition-colors">{L.cancel}</button>
            <button onClick={() => onReply(replyText, () => { setReplyText(""); setReplying(false); })} className="h-7 px-3 rounded-md text-xs font-medium bg-white text-black hover:bg-zinc-200 transition-colors" data-testid={`reply-submit-${comment.id}`}>{L.reply}</button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex gap-3">
          <button onClick={(e) => { e.stopPropagation(); setReplying(true); }} className="text-[11px] text-white/50 hover:text-white inline-flex items-center gap-1 transition-colors" data-testid={`reply-open-${comment.id}`}>
            <ReplyIcon className="h-3 w-3" /> {L.reply}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onResolve(); }} className="text-[11px] text-white/50 hover:text-emerald-400 inline-flex items-center gap-1 transition-colors">
            <Check className="h-3 w-3" /> {comment.resolved ? L.reopen : L.resolve}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-[11px] text-white/50 hover:text-red-400 inline-flex items-center gap-1 transition-colors">
            <Trash2 className="h-3 w-3" /> {L.del}
          </button>
        </div>
      )}
    </div>
  );
};
